import { booleanAttribute } from "#ce/utils/props";

// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow with an accessor onto the prop store. The `value`
// attribute seeds the calendar and names what a form reset restores.
export const props = Object.freeze({
  /**
   * name attribute used for the element's form value — required to participate in a native form submit.
   *
   * @type {String}
   * @default undefined
   */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Enable flatpickr's wrap mode. When set, the host shadow tree should also include trigger elements marked with the `data-toggle` / `data-clear` flatpickr attributes; clicks on those open and reset the calendar from outside the component.
   *
   * @type {Boolean}
   * @default false
   */
  wrap: {
    type: Boolean,
    default: false,
  },
  /**
   * Render the calendar inline instead of attaching to a hidden alt input. Required for the picker to be visible on its own — without `inline` (or a `wrap`-mode parent that drives flatpickr from outside), the calendar lives behind a hidden input and never paints.
   *
   * @type {Boolean}
   * @default false
   */
  inline: {
    type: Boolean,
    default: false,
  },
  /**
   * Selection mode. `"single"` is the default. `"range"` requires two clicks (start, then end); the change event only fires once both endpoints are picked. `"multiple"` lets the user toggle several individual dates.
   *
   * @type {String}
   * @default "single"
   * @values single, range, multiple
   * @storybook-control select
   */
  mode: {
    type: String,
    default: "single",
    validator: (value) => ["single", "range", "multiple"].includes(value),
  },
  /**
   * Convenience flag equivalent to `mode="range"`. Takes precedence over an explicit `mode` when truthy and `multiple` is not set.
   *
   * @type {Boolean}
   * @default false
   */
  range: {
    type: Boolean,
    default: false,
  },
  /**
   * Convenience flag equivalent to `mode="multiple"`. Takes precedence over both `range` and `mode` when truthy.
   *
   * @type {Boolean}
   * @default false
   */
  multiple: {
    type: Boolean,
    default: false,
  },
  /**
   * Date format string used when echoing the selected value through the `change` and `update-model-value` payloads. Uses flatpickr formatting tokens — `F j, Y` renders as `March 15, 2026`; `Y-m-d` keeps the value ISO.
   *
   * @type {String}
   * @default "F j, Y"
   */
  displayFormat: {
    type: String,
    default: "F j, Y",
  },
  /**
   * Workaround prop that injects an extra stylesheet link into the shadow root. Exists only until the design-token theme work lands and will be removed — do not rely on it in new code.
   *
   * @type {String}
   * @default undefined
   */
  tmpCssLink: {
    type: String,
    default: undefined,
  },
  /**
   * Disable the calendar — dates cannot be selected and the picker renders in a disabled state.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Require a date before the form can be submitted. Presence-based, the way the platform reads a
   * boolean attribute: any value other than `false` constrains the field.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required field is submitted without a date. Names what this
   * particular field is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` attribute on flatpickr's visible (alt) input. Pass a
   * catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a shared `data-testid` attribute on every rendered calendar day cell.
   * Pass a catalog value from the feature-specific caller (see `app/frontend/src/test-ids.json`).
   *
   * @type {String}
   * @default undefined
   */
  dayTestId: {
    type: String,
    default: undefined,
  },
});
